// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import Video, { VideoStyles } from "@werner/App/Components/Media/YoutubeVideo";
import { xScreen } from "@werner/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

export const enum fields {
    title = "title",
    src = "src",
    autoPlay = "autoPlay",
    addContent = "addContent",
    content = "content",
    contentId = "contentId",
}
interface VideoWidgetProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.src]: string;
        [fields.autoPlay]: boolean;
        [fields.addContent]: boolean;
        [fields.content]: string;
        [fields.contentId]: string;
    };
}
type Props = VideoWidgetProps;
interface VideoWidgetStyles {
    container?: InjectableCss;
    contentWrapper?: InjectableCss;
    videoStyles?: VideoStyles;
}
export const videoWidgetStyles: VideoWidgetStyles = {
    container: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            ${xScreen("lt", "md")} {
                flex-direction: column;
            }
        `,
    },
    contentWrapper: {
        css: css`
            padding: 44px 0 44px 74px;
            flex: 1;
            ${xScreen("lt", "md")} {
                padding: 22px 0 0 0;
            }
        `,
    },
    videoStyles: {
        wrapper: {
            css: css`
                width: 100%;
                max-width: 520px;
                min-height: 290px;
                & > iframe {
                    min-height: 290px;
                }
            `,
        },
    },
};

const styles = videoWidgetStyles;

const VideoWidget: React.FC<Props> = ({ fields: { src, autoPlay, addContent, content, contentId, title}, id }) => {
    return (
        <StyledWrapper {...styles.container}>
            <Video src={src} autoPlay={autoPlay} extendedStyles={styles.videoStyles} />
            {addContent && (
                <StyledWrapper {...styles.contentWrapper}>
                    {parse(content, parserOptions)}
                    <XngZone
                        zoneName={`ButtonZone_${title}`}
                        contentId={contentId || id}
                        hint="Zone For Button"
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    icon: "presentation-screen",
    fieldDefinitions: [
        {
            displayName: "Title",
            name: fields.title,
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            isVisible: item => false,
        },
        {
            displayName: "Video Src",
            name: fields.src,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            displayName: "Auto Play",
            name: fields.autoPlay,
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            displayName: "Add Content",
            name: fields.addContent,
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            displayName: "Content",
            name: fields.content,
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            isVisible: item => item.fields.addContent,
        },
    ],
};
const widgetModule: WidgetModule = {
    component: VideoWidget,
    definition,
};
export default widgetModule;
